'use client';

import { SxProps, Theme } from '@mui/material';
import { ReactElement, ReactNode } from 'react';
import BiometricConfidenceMeter from './BiometricConfidenceMeter';
import ConfidenceCardLayout from './ConfidenceCardLayout';
import { biometricConfidenceLevelName } from './ladder';
import { biometricConfidenceStatement } from './statements';
import { TBiometricConfidenceLevel } from './types';

/**
 * The card's three states, as a discriminated union rather than optional props.
 *
 * A loading or failed card has no level and no timestamp, and neither can be invented. Modelling
 * that as optional fields would let a caller pass a level with `status="loading"`, or omit one with
 * `status="ready"`, and nothing would complain. The union makes both impossible to write.
 *
 * The discriminant is a string rather than a boolean pair for the reason `PermissionBlock` records:
 * this repo builds with `strictNullChecks: false`, under which TypeScript does not narrow a
 * boolean-literal union, so the absent-field branch keeps the full union and the field fails to
 * resolve. A string discriminant narrows correctly under the same settings.
 *
 * `status` is optional on the ready arm so every existing caller keeps working unchanged.
 */
type TBiometricConfidenceCardState =
  | {
      status?: 'ready';
      level: TBiometricConfidenceLevel;
      /** When the level shown was last determined. */
      lastUpdatedOn: string;
    }
  | { status: 'loading' }
  | { status: 'error' };

export type TBiometricConfidenceCardProps = TBiometricConfidenceCardState & {
  /**
   * A slot for buttons. The prototype gives this card `VIEW EVIDENCE` and `REQUEST AGAIN`, with
   * `COPY REQUEST LINK` on its own row beneath them, and fills `VIEW EVIDENCE` only on a `Low`
   * outcome because that is the one a client has to act on.
   */
  actions?: ReactNode;
  /**
   * A statement for a state that offers nothing to act on, rendered where the action row sits.
   *
   * Passed *instead of* `actions`, not alongside them: the view that composes this card drops the
   * request control on a refusal, and explains itself only when dropping it leaves the row empty.
   * See `ConfidenceCardLayout`'s own docblock for why both render if both arrive.
   */
  notice?: ReactNode;
  /** A slot for supporting imagery, typically a biometric capture thumbnail. */
  media?: ReactNode;
  sx?: SxProps<Theme>;
  'data-testid'?: string;
};

const HEADING = 'Biometric checks';
const HELP_TEXT =
  'Reflects the combined outcome of the checks performed on this biometric capture.';

/**
 * The biometric check card: heading, meter, level name and statement, and a timestamp.
 *
 * There is no `title` or `helpText` prop here, unlike a generic card. This component owns its own
 * heading ("Biometric checks") and tooltip copy, which is the whole reason it exists separately
 * from `DocumentConfidenceCard` rather than as one card taking an `axis` prop: two fixed headings
 * cannot be expressed as a single optional prop without a caller being able to get them wrong or
 * omit them.
 *
 * **The heading and its tooltip render in all three states.** Which axis a card is showing is known
 * before any request is made, so skeletoning it would hide something we already have and make the
 * two loading cards indistinguishable from each other.
 */
export default function BiometricConfidenceCard({
  actions,
  notice,
  media,
  sx,
  'data-testid': dataTestId,
  ...state
}: Readonly<TBiometricConfidenceCardProps>): ReactElement {
  // A guard clause rather than a ternary per prop, and narrowing on `state.status` directly rather
  // than through an `isReady` alias: under `strictNullChecks: false` an aliased condition is not a
  // dependable discriminant, so `state.level` inside the ready branch would not resolve.
  if (state.status === 'loading' || state.status === 'error') {
    return (
      <ConfidenceCardLayout
        heading={HEADING}
        helpText={HELP_TEXT}
        status={state.status}
        actions={actions}
        notice={notice}
        media={media}
        sx={sx}
        data-testid={dataTestId}
      />
    );
  }

  return (
    <ConfidenceCardLayout
      heading={HEADING}
      helpText={HELP_TEXT}
      meter={<BiometricConfidenceMeter level={state.level} />}
      levelName={biometricConfidenceLevelName(state.level)}
      statement={biometricConfidenceStatement(state.level)}
      actions={actions}
      notice={notice}
      media={media}
      lastUpdatedOn={state.lastUpdatedOn}
      sx={sx}
      data-testid={dataTestId}
    />
  );
}
